import http from '../../shared/http.js';
import { initLiveList } from '../../shared/liveList.js';
import {
  confirmAction,
  hideLoading,
  notifyError,
  notifySuccess,
  showLoading
} from '../../shared/notify.js';

// The Zefix case manager (WOR-LI-G44): the queue of customers whose UID the
// sweep could not settle on its own.
//
// Three decisions. Two act straight away; "von Hand suchen" needs a search term
// first, so it opens the dialog in faelle.pug - one dialog for the page, filled
// in with whichever case it was opened for, rather than one per card.
//
// Every one of them ends with a refresh rather than a local DOM edit: the
// server owns the markup, a decision usually removes the card from the queue
// altogether, and the counts in the pager have to move with it.

// What the two immediate decisions do, so the handler stays a lookup rather
// than a chain of ifs.
const ACTIONS = {
  uebernehmen: {
    confirm: (button) => ({
      title: 'UID übernehmen',
      message: `"${button.dataset.firma}" als Firma von ${button.dataset.kunde} übernehmen? Die UID ${button.dataset.uid} wird eingetragen.`,
      okText: 'Übernehmen'
    }),
    loading: 'UID wird übernommen …',
    done: 'Die UID wurde eingetragen.',
    run: (id, button) => http.post(`/zefix/faelle/${id}/uebernehmen`, { uid: button.dataset.uid })
  },
  ignorieren: {
    confirm: (button) => ({
      title: 'Keine UID',
      message: `${button.dataset.kunde} hat keine UID im Handelsregister? Der Fall wird abgelegt und der Abgleich fragt nicht wieder nach.`,
      okText: 'Ablegen'
    }),
    loading: 'Fall wird abgelegt …',
    done: 'Der Fall wurde abgelegt.',
    run: (id) => http.post(`/zefix/faelle/${id}/ignorieren`)
  }
};

// The API's own text is worth showing verbatim here: every refusal these
// endpoints make is something the person can act on - somebody already worked
// this case, the customer already has a different UID, another customer holds
// this one, Zefix is rate-limited right now.
function fehlermeldung(err) {
  return err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
}

export function initZefixFaelle() {
  const live = initLiveList({
    results: 'zefix-fall-list',
    path: '/zefix/faelle',
    forms: ['zefix-fall-filter']
  });
  if (!live) return;

  const list = document.getElementById('zefix-fall-list');
  const dialog = document.getElementById('zefix-suche-dialog');
  const form = document.getElementById('zefix-suche-form');
  const begriffFeld = document.getElementById('zefix-suche-begriff');
  const kundeAnzeige = document.getElementById('zefix-suche-kunde');

  // Which case the dialog is open for. Held here rather than on the dialog,
  // because the cards are replaced on every refresh and a dataset on a row that
  // no longer exists would be a stale id.
  let offenerFall = null;

  // Delegated, because liveList.js replaces the cards on every filter, search
  // and page change - handlers bound to a button would go with them.
  list.addEventListener('click', async (event) => {
    const button = event.target.closest('button[data-aktion]');
    if (!button) return;

    const { aktion, id } = button.dataset;

    if (aktion === 'suchen') {
      offenerFall = id;
      kundeAnzeige.textContent = button.dataset.kunde || '';
      // Prefilled with the term that failed: the fix is almost always an edit
      // to it - a word too many, a legal form the register does not carry -
      // rather than something typed from scratch.
      begriffFeld.value = button.dataset.suchbegriff || '';
      dialog.showModal();
      begriffFeld.focus();
      begriffFeld.select();
      return;
    }

    const handler = ACTIONS[aktion];
    if (!handler) return;

    if (!(await confirmAction(handler.confirm(button)))) return;

    showLoading(handler.loading);
    try {
      await handler.run(id, button);
      hideLoading();
      notifySuccess(handler.done);
      await live.refresh();
    } catch (err) {
      hideLoading();
      notifyError(fehlermeldung(err));
    }
  });

  // The dialog's own cancel button. The backdrop form closes it on its own,
  // being method="dialog".
  dialog.addEventListener('click', (event) => {
    if (event.target.closest('[data-close="zefix-suche-dialog"]')) dialog.close();
  });

  form.addEventListener('submit', async (event) => {
    // The form is method="dialog", which would close it before the request is
    // even sent - and a failed search has to leave the term on screen to be
    // corrected rather than making the person type it again.
    event.preventDefault();
    if (!offenerFall) return;

    const suchbegriff = begriffFeld.value.trim();
    if (suchbegriff.length < 3) {
      notifyError('Der Suchbegriff braucht mindestens drei Zeichen.');
      return;
    }

    showLoading('Zefix wird abgefragt …');
    try {
      const antwort = await http.post(`/zefix/faelle/${offenerFall}/suchen`, { suchbegriff });
      hideLoading();
      dialog.close();
      offenerFall = null;

      // Axios wraps the body in `data`, and the API wraps the payload in `data`
      // again - so the count sits two levels down.
      const treffer = antwort?.data?.data?.treffer || 0;
      notifySuccess(
        treffer === 0
          ? 'Zefix kennt diesen Namen nicht.'
          : `Zefix hat ${treffer === 1 ? 'einen Treffer' : `${treffer} Treffer`} geliefert.`
      );
      await live.refresh();
    } catch (err) {
      hideLoading();
      notifyError(fehlermeldung(err));
    }
  });
}
